import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { fetchGalleryByTitle, fetchServiceByTitle } from '../helpers/contentfulHelper';
import styles from '../styles/StairsPage.module.css';

const WorkshopPage = () => {
  const [workshopImages, setWorkshopImages] = useState([]);
  const [isGalleryLoading, setIsGalleryLoading] = useState(true);
  const [serviceCopy, setServiceCopy] = useState(null);
  const [galleryCaption, setGalleryCaption] = useState('Workshop');
  const [activeIndex, setActiveIndex] = useState(null);
  const hasLightbox = activeIndex !== null;

  useEffect(() => {
    const loadGallery = async () => {
      try {
        const entry = await fetchGalleryByTitle('Workshop Gallery');
        const contentfulImages = entry?.fields?.images || [];
        const caption = entry?.fields?.caption;

        if (caption) {
          setGalleryCaption(caption);
        }

        const mapped = contentfulImages
          .map((img) => {
            const url = img?.fields?.file?.url;
            if (!url) return null;

            return {
              src: `https:${url}`,
              alt: img?.fields?.title || 'Workshop gallery image',
            };
          })
          .filter(Boolean);

        setWorkshopImages(mapped);
      } finally {
        setIsGalleryLoading(false);
      }
    };

    loadGallery();
  }, []);

  useEffect(() => {
    const loadServiceCopy = async () => {
      const service = await fetchServiceByTitle('Workshop');
      if (service?.fields?.copy) {
        setServiceCopy(service.fields.copy);
      }
    };

    loadServiceCopy();
  }, []);

  const openLightbox = (index) => setActiveIndex(index);
  const closeLightbox = () => setActiveIndex(null);
  const goPrev = () =>
    setActiveIndex((prev) =>
      prev === null ? prev : prev === 0 ? workshopImages.length - 1 : prev - 1
    );
  const goNext = () =>
    setActiveIndex((prev) =>
      prev === null ? prev : prev === workshopImages.length - 1 ? 0 : prev + 1
    );

  return (
    <section className={styles.page}>
      <div className={styles.container}>
        <header className={styles.header}>
          <p className={styles.kicker}>In the Workshop</p>
          <h1>{galleryCaption}</h1>
          {serviceCopy ? (
            <div className={styles.headerCopy}>
              {documentToReactComponents(serviceCopy)}
            </div>
          ) : (
            <p>
              A closer look at how we design, machine, assemble and finish each piece
              in-house using traditional bench joinery methods.
            </p>
          )}
        </header>

        <div className={styles.imageGrid}>
          {isGalleryLoading &&
            Array.from({ length: 6 }).map((_, index) => (
              <div key={`workshop-skeleton-${index}`} className={styles.skeletonCard} />
            ))}

          {!isGalleryLoading &&
            workshopImages.map((image, index) => (
              <button
                key={image.src}
                type="button"
                className={styles.imageButton}
                onClick={() => openLightbox(index)}
                aria-label={`Open image ${index + 1}`}
              >
                <img src={image.src} alt={image.alt} />
              </button>
            ))}

          {!isGalleryLoading && workshopImages.length === 0 && (
            <p className={styles.emptyState}>Gallery images coming soon.</p>
          )}
        </div>

        <div className={styles.ctaWrap}>
          <h3>Want to talk through your project?</h3>
          <p>We can advise on materials, timelines and practical options.</p>
          <Link to="/contact">Make an Enquiry</Link>
        </div>

        {hasLightbox && (
          <div className={styles.lightbox} role="dialog" aria-modal="true">
            <button
              type="button"
              className={`${styles.navButton} ${styles.prevButton}`}
              onClick={goPrev}
              aria-label="Previous image"
            >
              &#8249;
            </button>
            <div className={styles.lightboxInner}>
              <button
                type="button"
                className={styles.closeButton}
                onClick={closeLightbox}
                aria-label="Close gallery"
              >
                &#10005;
              </button>
              <img
                src={workshopImages[activeIndex].src}
                alt={workshopImages[activeIndex].alt}
                className={styles.lightboxImage}
              />
              <div className={styles.thumbRow}>
                {workshopImages.map((image, index) => (
                  <button
                    key={`${image.src}-thumb`}
                    type="button"
                    className={`${styles.thumbButton} ${
                      index === activeIndex ? styles.thumbActive : ''
                    }`}
                    onClick={() => openLightbox(index)}
                    aria-label={`View image ${index + 1}`}
                  >
                    <img src={image.src} alt="" />
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              className={`${styles.navButton} ${styles.nextButton}`}
              onClick={goNext}
              aria-label="Next image"
            >
              &#8250;
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default WorkshopPage;
